import type { RouteMeta, RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'
import type { BreadcrumbItem } from '@/app/router/meta'

/**
 * The invoices module's routes (WP-08, wp/invoices §3): the ten manifest rows, paths and names from it (D-15); the
 * legacy paths (`/ac/invoices/preview/:id`, `/ac/invoices/create/:type?`, `/invoice/:id/h/:hash`, …) reach them through
 * the generated legacy redirects. `/ac/payments` redirects to `/ac/invoices` (D-39; the `payments` name is gone).
 *
 * NEW-43: legacy `meta.module` / `mode` (`finances`, `payments`, `bank`) were never evaluated. The guard checks
 * `resource` (D-21): every employer route takes Invoicing, the permission the API asks for every read, and write on
 * the five editors (FX-052). The preview component owns the three preview routes (OPEN-4 (a)); its "Edit" goes by
 * record (NEW-30). The payer page is public (`meta.public`, full layout) and calls the API without `Auth-Key`.
 */
const crumb = (text: string, to?: string): BreadcrumbItem => (to ? { text, to } : { text, active: true })

const invoices = crumb(keys.t('invoices.route.title.invoices'), '/ac/invoices')

const InvoiceListView = () => import('./views/InvoiceListView.vue')
const InvoicePreviewView = () => import('./views/InvoicePreviewView.vue')
const InvoiceEditView = () => import('./views/InvoiceEditView.vue')
const CreditNoteEditView = () => import('./views/CreditNoteEditView.vue')
const InvoicePublicView = () => import('./views/InvoicePublicView.vue')

const preview = (overrides: Partial<RouteMeta>): RouteMeta => ({
  software: 'ac',
  resource: 'invoices',
  pageTitle: keys.t('invoices.route.title.invoicePreview'),
  ...overrides,
})

const createInvoiceMeta: RouteMeta = {
  software: 'ac',
  resource: 'invoices',
  action: 'write',
  navActiveLink: 'invoices',
  pageTitle: keys.t('invoices.route.title.invoices'),
  breadcrumb: [invoices, crumb(keys.t('invoices.route.title.createInvoice'))],
}

const routes: RouteRecordRaw[] = [
  {
    path: '/ac/invoices',
    name: 'invoices',
    component: InvoiceListView,
    meta: {
      software: 'ac',
      resource: 'invoices',
      pageTitle: keys.t('invoices.route.title.invoices'),
      breadcrumb: [crumb(keys.t('invoices.route.title.invoices'))],
    },
  },
  {
    path: '/ac/invoices/:id(\\d+)',
    name: 'invoice-preview',
    component: InvoicePreviewView,
    meta: preview({
      navActiveLink: 'invoices',
      breadcrumb: [invoices, crumb(keys.t('invoices.route.title.previewInvoice'))],
    }),
  },
  {
    path: '/ac/expenses/:id(\\d+)',
    name: 'expense-preview',
    component: InvoicePreviewView,
    meta: preview({
      navActiveLink: 'expenses',
      pageTitle: keys.t('invoices.route.title.expensePreview'),
      breadcrumb: [
        crumb(keys.t('invoices.route.title.expenses'), '/ac/expenses'),
        crumb(keys.t('invoices.route.title.previewExpense')),
      ],
    }),
  },
  {
    path: '/ac/finances/documents/:id(\\d+)',
    name: 'finance-preview',
    component: InvoicePreviewView,
    meta: preview({
      navActiveLink: 'finances',
      breadcrumb: [
        crumb(keys.t('invoices.route.title.finances'), '/ac/finances'),
        crumb(keys.t('invoices.route.title.previewInvoice')),
      ],
    }),
  },
  {
    path: '/ac/invoices/new/:type(\\d+)?',
    name: 'create-invoice',
    component: InvoiceEditView,
    meta: createInvoiceMeta,
  },
  {
    path: '/ac/invoices/new/from-quote/:quoteId(\\d+)?',
    name: 'create-quote-invoice',
    component: InvoiceEditView,
    meta: createInvoiceMeta,
  },
  {
    path: '/ac/invoices/:id(\\d+)/edit',
    name: 'edit-invoice',
    component: InvoiceEditView,
    meta: {
      ...createInvoiceMeta,
      pageTitle: keys.t('invoices.route.title.editInvoice'),
      breadcrumb: [invoices, crumb(keys.t('invoices.route.title.editInvoice'))],
    },
  },
  {
    path: '/ac/credit-notes/new/:invoiceId(\\d+)?',
    name: 'create-credit-note',
    component: CreditNoteEditView,
    meta: {
      ...createInvoiceMeta,
      breadcrumb: [invoices, crumb(keys.t('invoices.route.title.createCreditNote'))],
    },
  },
  {
    path: '/ac/credit-notes/:id(\\d+)/edit',
    name: 'edit-credit-note',
    component: CreditNoteEditView,
    meta: {
      ...createInvoiceMeta,
      pageTitle: keys.t('invoices.route.title.editInvoice'),
      breadcrumb: [invoices, crumb(keys.t('invoices.route.title.editCreditNote'))],
    },
  },
  {
    path: '/invoices/:id(\\d+)/pay/:hash',
    name: 'invoice-public',
    component: InvoicePublicView,
    meta: { layout: 'full', public: true, pageTitle: keys.t('invoices.route.title.yourInvoice') },
  },
]

export default routes
